import { useTranslation } from 'react-i18next';
import type { EntityImportConfig } from '../types';
import { importFieldLabel } from '../importI18n';

interface MappingStepProps {
  config: EntityImportConfig;
  headers: string[];
  mapping: Record<string, string>;
  uomLabels?: string[];
  onChange: (fieldname: string, column: string) => void;
}

export default function MappingStep({
  config,
  headers,
  mapping,
  uomLabels = [],
  onChange,
}: MappingStepProps) {
  const { t } = useTranslation();
  const entityKey = config.entityId;

  const missingRequired = config.fields
    .filter((f) => f.required && !mapping[f.fieldname])
    .map((f) => importFieldLabel(t, f.fieldname, f.label));

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-lg font-bold text-gray-900 mb-1">
          {t(`import.${entityKey}.step2Title`, { defaultValue: 'Step 2: Map Columns' })}
        </h4>
        <p className="text-gray-600 text-sm">
          {t(`import.${entityKey}.step2Help`, {
            defaultValue: 'Match each system field to a column from your file. Required fields must be mapped.',
          })}
        </p>
      </div>

      {missingRequired.length > 0 && (
        <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
          {t(`import.${entityKey}.mapRequired`, { defaultValue: 'Map required fields before continuing:' })}{' '}
          {missingRequired.join(', ')}
        </div>
      )}

      {config.showUomGuide && uomLabels.length > 0 && (
        <div className="p-3 rounded-lg bg-primary-50 border border-primary-100 text-primary-900 text-sm">
          <span className="font-medium">{t('import.availableUoms')}</span> {uomLabels.join(', ')}
        </div>
      )}

      <div className="overflow-x-auto border border-gray-200 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 font-semibold text-gray-900">{t('import.systemField')}</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-900">{t('import.fileColumn')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {config.fields.map((field) => (
              <tr key={field.fieldname}>
                <td className="px-4 py-3 text-gray-800 align-top">
                  <div>
                    {importFieldLabel(t, field.fieldname, field.label)}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </div>
                  {field.hint && (
                    <p className="text-xs text-gray-500 mt-1 max-w-xs">{field.hint}</p>
                  )}
                </td>
                <td className="px-4 py-3">
                  <select
                    className="input-field"
                    value={mapping[field.fieldname] || ''}
                    onChange={(e) => onChange(field.fieldname, e.target.value)}
                  >
                    <option value="">{t('import.skipColumn')}</option>
                    {headers.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
